import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Copy, Loader2, MessageSquare, Send, AlertOctagon, Star } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StatusBadge from "../components/StatusBadge";
import VerifiedBadge from "../components/VerifiedBadge";
import StripePayment from "../components/StripePayment";
import { PageLoading, ErrorState } from "../components/States";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDateTime, fmtDateTimeZone, fmtMoney, fmtDuration, setPageTitle } from "../lib/format";

export default function BookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [bk, setBk] = useState(null);
  const [otp, setOtp] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [reviewState, setReviewState] = useState(null);
  const [draft, setDraft] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelPrev, setCancelPrev] = useState(null); // server-computed cancellation quote
  const [myVehicles, setMyVehicles] = useState(null);
  const [vehChangeOpen, setVehChangeOpen] = useState(false);
  const [vehNewId, setVehNewId] = useState("");
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const b = await api.getBooking(id);
      setBk(b);
      setError(null);
      if (["authorized", "paid"].includes(b.status)) {
        try { setOtp(await api.getOtp(id)); } catch { /* host side has no OTP read */ }
      }
      if (b.status === "paid") {
        try { setMessages(await api.bookingChat(id)); } catch { /* chat locked */ }
        try { setReviewState(await api.bookingReview(id)); } catch { setReviewState(null); }
      }
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => {
    setPageTitle("Booking");
    load();
    pollRef.current = setInterval(load, 15000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const copyOtp = () => { if (otp?.otp) { navigator.clipboard?.writeText(otp.otp); toast.success("Code copied"); } };

  const retryCapture = async () => {
    setBusy(true);
    try { await api.retryCapture(id); toast.success("Payment captured"); await load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const openCancel = async () => {
    setBusy(true);
    try { setCancelPrev(await api.cancelPreview(id)); setCancelOpen(true); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const confirmCancel = async () => {
    setBusy(true);
    try {
      const r = await api.cancelBooking(id);
      setCancelOpen(false);
      toast.success(r?.policy_pending
        ? "Booking cancelled — refund pending policy review, we'll notify you"
        : r?.refund_cents > 0
        ? `Booking cancelled — a refund of ${(r.refund_cents / 100).toFixed(2)} was initiated`
        : r?.refund_percent === 100 ? "Booking cancelled — no charge" : "Booking cancelled");
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const reportIssue = async () => {
    const reason = window.prompt("What happened? (spot_unavailable / host_unreachable / wrong_location / other)", "host_unreachable");
    if (!reason) return;
    setBusy(true);
    try { await api.reportIssue(id, reason.trim(), ""); toast.success("Issue reported — support will review it. You won't be charged unless the OTP is verified."); await load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // One-time vehicle change during an ACTIVE session — the server owns the counter.
  const openVehicleChange = () => {
    setVehChangeOpen(true);
    if (!myVehicles) {
      api.myVehicles().then((vs) => { setMyVehicles(vs); if (vs[0]) setVehNewId(vs[0].vehicle_id); }).catch(() => setMyVehicles([]));
    }
  };
  const changeVehicle = async () => {
    if (!vehNewId) return;
    setBusy(true);
    try {
      await api.changeBookingVehicle(id, vehNewId);
      toast.success("Vehicle updated — the host sees the new plate.");
      setVehChangeOpen(false);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    try { await api.sendBookingMessage(id, draft.trim()); setDraft(""); setMessages(await api.bookingChat(id)); }
    catch (err) { toast.error(err.message); }
  };

  const isDriver = bk && user && bk.driver_id === user.user_id;

  return (
    <div className="min-h-screen flex flex-col" data-testid="booking-detail-page">
      <Navbar />
      <main className="flex-1 max-w-5xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <Link to="/bookings" className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.14em] text-zinc-500 hover:text-white transition-colors" data-testid="back-to-bookings">
          <ChevronLeft className="w-4 h-4" /> All bookings
        </Link>
        {error && !bk ? <div className="mt-8"><ErrorState body={error} onRetry={load} /></div> : !bk ? <div className="mt-8"><PageLoading label="Loading booking…" /></div> : (
          <div className="mt-8 grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3 space-y-10">
              <div>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h1 className="type-h2" data-testid="booking-spot-title">{bk.spot_title}</h1>
                    <p className="mt-2 text-sm text-zinc-500">{bk.spot_address}</p>
                    {bk.approximate_location && <p className="mt-1 text-xs text-zinc-600" data-testid="booking-approx-note">Approximate area — the exact address appears once your payment is authorized.</p>}
                  </div>
                  <StatusBadge value={bk.status} />
                </div>
                {["authorized", "paid", "completed"].includes(bk.status) && <LiveStatusStrip bk={bk} />}
                <dl className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-6 border-t border-[#18181B] pt-8">
                  <div><dt className="meta-label">Start</dt><dd className="mt-1.5 text-sm font-medium" data-testid="booking-start-time">{fmtDateTimeZone(bk.start_time, bk.timezone)}</dd></div>
                  <div><dt className="meta-label">End</dt><dd className="mt-1.5 text-sm font-medium" data-testid="booking-end-time">{fmtDateTimeZone(bk.end_time, bk.timezone)}</dd></div>
                  <div><dt className="meta-label">Duration</dt><dd className="mt-1.5 text-sm font-medium">{fmtDuration(bk.start_time, bk.end_time)}</dd></div>
                  <div><dt className="meta-label">Total</dt><dd className="mt-1.5 font-display font-bold text-lg" data-testid="booking-total">{fmtMoney(bk.total)}</dd></div>
                  {(bk.property_code_snapshot || bk.property_code) && (
                    <div><dt className="meta-label">Property code</dt><dd className="mt-1.5 font-mono font-bold tracking-[0.2em] text-[#FF3B45]" data-testid="booking-property-code">{bk.property_code_snapshot || bk.property_code}</dd></div>
                  )}
                  {bk.commercial && bk.category_name && (
                    <div><dt className="meta-label">Spot type</dt><dd className="mt-1.5 text-sm text-zinc-300" data-testid="booking-category">{bk.category_name}{bk.property_spot_ref ? ` · Spot ${bk.property_spot_ref}` : ""}</dd></div>
                  )}
                  {bk.fee_rate_applied != null && (
                    <>
                      <div><dt className="meta-label">Parking{bk.pricing_tier ? ` · ${bk.pricing_tier} rate` : ""}</dt><dd className="mt-1.5 text-sm text-zinc-300" data-testid="booking-parking">{fmtMoney(bk.subtotal, (bk.currency || "usd").toUpperCase())}</dd></div>
                      <div><dt className="meta-label">Service fee ({Math.round(bk.fee_rate_applied * 100)}%)</dt><dd className="mt-1.5 text-sm text-zinc-300" data-testid="booking-fee">{fmtMoney(bk.platform_fee, (bk.currency || "usd").toUpperCase())}</dd></div>
                      {(bk.payment_handling_cents > 0) && (
                        <div><dt className="meta-label">Payment processing fee</dt><dd className="mt-1.5 text-sm text-zinc-300" data-testid="booking-processing">{fmtMoney(bk.payment_handling_cents / 100, (bk.currency || "usd").toUpperCase())}</dd></div>
                      )}
                    </>
                  )}
                  <div><dt className="meta-label">Driver</dt><dd className="mt-1.5 text-sm text-zinc-300 flex items-center gap-2 flex-wrap">{bk.driver_name || bk.driver_email || "—"}<VerifiedBadge verified={bk.driver_id_verified} testId="driver-id-verified-badge" /></dd></div>
                  <div><dt className="meta-label">Host</dt><dd className="mt-1.5 text-sm text-zinc-300 flex items-center gap-2 flex-wrap">{bk.host_name || bk.host_email || "—"}<VerifiedBadge verified={bk.host_id_verified} testId="host-id-verified-badge" /></dd></div>
                  {bk.vehicle_snapshot && (
                    <div className="sm:col-span-2" data-testid="booking-vehicle">
                      <dt className="meta-label">Vehicle</dt>
                      <dd className="mt-1.5 text-sm text-zinc-300">
                        {[bk.vehicle_snapshot.nickname, [bk.vehicle_snapshot.make, bk.vehicle_snapshot.model].filter(Boolean).join(" "), bk.vehicle_snapshot.colour].filter(Boolean).join(" · ")}
                        {bk.vehicle_snapshot.plate && <span className="font-mono text-zinc-400"> · {bk.vehicle_snapshot.plate}</span>}
                      </dd>
                    </div>
                  )}
                </dl>
                {bk.access_instructions && (
                  <div className="mt-8 p-5 rounded-xl bg-emerald-500/5 border border-emerald-500/20" data-testid="access-instructions">
                    <p className="meta-label !text-emerald-400">Access instructions</p>
                    <p className="mt-2 text-sm text-zinc-200 whitespace-pre-line leading-relaxed">{bk.access_instructions}</p>
                  </div>
                )}
                <p className="mt-6 text-xs text-zinc-500" data-testid="park-designated-note">Park only in the designated MyRedPark space.</p>
              </div>

              {(bk.status === "paid" || bk.status === "refunded") && (
                <div className="surface-flat p-6" data-testid="booking-chat">
                  <h2 className="type-h3 flex items-center gap-2.5"><MessageSquare className="w-4 h-4 text-[#FF3B45]" /> Messages</h2>
                  <div className="mt-5 space-y-3 max-h-72 overflow-auto">
                    {messages.length === 0 && <p className="text-sm text-zinc-600">No messages yet — say hello.</p>}
                    {messages.map((m) => (
                      <div key={m.message_id} className={`max-w-[80%] px-4 py-2.5 rounded-xl text-sm leading-relaxed ${m.sender_id === user?.user_id ? "ml-auto bg-[#E50914] text-white" : "bg-[#080B0D] border border-[#27272A]"}`}>
                        <p className="text-[10px] opacity-70 mb-0.5">{m.sender_name}</p>{m.text}
                      </div>
                    ))}
                  </div>
                  {bk.session_completed_at || bk.status === "refunded" ? (
                    <p data-testid="chat-readonly-note" className="mt-4 text-xs text-zinc-500 border border-[#27272A] rounded-xl px-4 py-3">
                      This booking has ended. New messages are no longer available.
                    </p>
                  ) : (
                  <form onSubmit={send} className="mt-4 flex gap-2">
                    <input data-testid="chat-input" value={draft} onChange={(e) => setDraft(e.target.value)} className="input-dark flex-1" placeholder="Write a message…" aria-label="Message" />
                    <button type="submit" data-testid="chat-send-btn" className="btn-primary !px-4" aria-label="Send"><Send className="w-4 h-4" /></button>
                  </form>
                  )}
                </div>
              )}
            </div>

            <div className="lg:col-span-2 space-y-6">
              {bk.session_started_at && !bk.session_completed_at && bk.status === "paid" && (
                <>
                  <ParkingTimer bk={bk} />
                  <ExtensionPanel bk={bk} onDone={load} />
                </>
              )}
              {bk.session_completed_at && (
                <div className="surface-flat p-6 text-center" data-testid="session-ended-card">
                  <p className="meta-label">Parking session</p>
                  <p className="mt-2 text-sm text-zinc-300 font-semibold">Session ended</p>
                </div>
              )}
              {isDriver && bk.status === "paid" && reviewState && (
                <ReviewCard bookingId={bk.booking_id} state={reviewState} onDone={load} />
              )}
              {isDriver && otp?.otp && bk.status === "authorized" && (
                <div className="surface-flat p-7 text-center" data-testid="otp-panel">
                  <p className="meta-label">Your arrival code</p>
                  <button onClick={copyOtp} data-testid="otp-display" aria-label="Copy arrival code"
                          className="font-mono-otp text-4xl tracking-[0.35em] bg-[#050607] text-[#FF3B45] border border-[#E50914]/40 rounded-xl px-7 py-5 mt-4 inline-flex items-center gap-3 transition-colors hover:border-[#E50914]">
                    {otp.otp}<Copy className="w-4 h-4 text-zinc-600" />
                  </button>
                  <p className="mt-4 text-xs text-zinc-600 leading-relaxed">Show this to the host on arrival. Payment completes only after they verify it.</p>
                </div>
              )}
              <div className="surface-flat p-6 space-y-3" data-testid="booking-actions">
                <p className="meta-label mb-1">Actions</p>
                {bk.capture_failed_at && bk.status === "authorized" && isDriver && (
                  <button onClick={retryCapture} disabled={busy} data-testid="retry-capture-btn" className="btn-primary w-full text-sm">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Retry Payment Capture
                  </button>
                )}
                {["pending", "pending_auth", "pending_payment"].includes(bk.status) && isDriver && (
                  <>
                    {/* Continue an unpaid hold (§7): resumes the ORIGINAL booking
                        in place — never a new booking, never a new PaymentIntent. */}
                    <ResumePayment bk={bk} onDone={load} />
                    <button onClick={openCancel} disabled={busy} data-testid="cancel-booking-btn" className="btn-secondary w-full text-sm">Cancel Booking</button>
                  </>
                )}
                {bk.status === "authorized" && (
                  <button onClick={openCancel} disabled={busy} data-testid="cancel-authorized-btn" className="btn-secondary w-full text-sm">Cancel booking</button>
                )}
                {isDriver && bk.status === "active" && (
                  (bk.vehicle_change_count || 0) === 0 ? (
                    <button onClick={openVehicleChange} disabled={busy} data-testid="change-vehicle-btn" className="btn-secondary w-full text-sm">Change vehicle (one-time)</button>
                  ) : (
                    <p className="text-xs text-zinc-600" data-testid="vehicle-change-used">Vehicle was already changed once for this booking.</p>
                  )
                )}
                {vehChangeOpen && isDriver && bk.status === "active" && (
                  <div className="rounded-xl border border-[#27272A] p-3.5 space-y-2.5" data-testid="vehicle-change-panel">
                    <p className="text-xs text-zinc-400">One-time change — the host sees the currently authorized plate.</p>
                    {myVehicles?.length > 0 ? (
                      <>
                        <select value={vehNewId} onChange={(e) => setVehNewId(e.target.value)} aria-label="New vehicle"
                                className="w-full rounded-xl border border-[#27272A] bg-[#0A0D10] px-3.5 py-2.5 text-sm text-white" data-testid="vehicle-change-select">
                          {myVehicles.map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.nickname || `${v.make} ${v.model}`} · {v.plate}</option>)}
                        </select>
                        <button onClick={changeVehicle} disabled={busy || !vehNewId} className="btn-primary w-full !py-2.5 text-xs" data-testid="vehicle-change-confirm">
                          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm vehicle change"}
                        </button>
                      </>
                    ) : (
                      <p className="text-xs text-zinc-500">No vehicles on your account — add one under My Vehicles first.</p>
                    )}
                  </div>
                )}
                {["authorized", "paid", "active", "completed"].includes(bk.status) && (
                  <Link to="/invoices" data-testid="view-invoice-btn" className="btn-secondary w-full text-sm">View invoice</Link>
                )}
                {["pending", "pending_auth", "authorized"].includes(bk.status) && isDriver && (
                  <button onClick={reportIssue} disabled={busy} data-testid="report-issue-btn"
                          className="w-full text-sm text-amber-400 border border-amber-500/30 hover:bg-amber-500/10 rounded-lg px-5 py-3 transition-colors inline-flex items-center justify-center gap-2">
                    <AlertOctagon className="w-4 h-4" /> Report a problem (no-show, wrong spot…)
                  </button>
                )}
                {bk.driver_reported_issue && <p className="text-xs text-amber-400/90 leading-relaxed">Issue reported — support is reviewing this booking.</p>}
                <p className="text-xs text-zinc-600 pt-1">Questions? <Link to="/support" className="text-[#FF3B45] hover:underline">Contact support</Link></p>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
      {/* Cancellation modal — every figure is the server-computed cancel-preview;
          the client never calculates refund amounts. */}
      {cancelOpen && cancelPrev && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
             role="dialog" aria-modal="true" aria-label="Cancel booking" data-testid="cancel-modal">
          <div className="w-full max-w-md rounded-2xl border border-[#27272A] bg-[#0D1114] p-6 shadow-2xl shadow-black/60">
            <p className="font-display font-bold text-lg">Cancel this booking?</p>
            {cancelPrev.free ? (
              <p className="mt-2.5 text-sm text-zinc-400 leading-relaxed" data-testid="cancel-free-note">
                {cancelPrev.note || "Free cancellation — nothing has been charged yet."}
              </p>
            ) : cancelPrev.policy_pending ? (
              <div className="mt-4 p-4 rounded-xl border border-amber-500/25 bg-amber-500/5" data-testid="cancel-policy-pending">
                <p className="text-sm text-amber-300 leading-relaxed">
                  This cancellation is in the 48–72 hour notice window. The booking is cancelled
                  immediately and the spot released, but the refund percentage for this window is
                  under policy review — our team processes it manually and you will be notified of
                  the outcome.
                </p>
                <p className="mt-3 text-xs text-zinc-500">
                  Eligible base fare: {fmtMoney((cancelPrev.eligible_base_cents || 0) / 100, (cancelPrev.currency || "usd").toUpperCase())}
                  {" "}· Service and payment processing fees are always non-refundable.
                </p>
              </div>
            ) : (
              <div className="mt-4 space-y-2 text-sm" data-testid="cancel-refund-breakdown">
                <div className="flex justify-between text-emerald-400">
                  <span>Refund · {cancelPrev.refund_percent}% of the base fare</span>
                  <span data-testid="cancel-refund-amount">{fmtMoney((cancelPrev.refund_cents || 0) / 100, (cancelPrev.currency || "usd").toUpperCase())}</span>
                </div>
                <div className="flex justify-between text-zinc-500">
                  <span>Service fee (non-refundable)</span>
                  <span data-testid="cancel-fee-nonref">{fmtMoney((cancelPrev.non_refundable_service_fee_cents || 0) / 100, (cancelPrev.currency || "usd").toUpperCase())}</span>
                </div>
                <div className="flex justify-between text-zinc-500">
                  <span>Payment processing fee (non-refundable)</span>
                  <span data-testid="cancel-processing-nonref">{fmtMoney((cancelPrev.non_refundable_processing_cents || 0) / 100, (cancelPrev.currency || "usd").toUpperCase())}</span>
                </div>
                <div className="flex justify-between font-semibold text-white border-t border-[#1F2328] pt-2">
                  <span>Charged on cancellation</span>
                  <span data-testid="cancel-retained-amount">{fmtMoney((cancelPrev.retained_cents || 0) / 100, (cancelPrev.currency || "usd").toUpperCase())}</span>
                </div>
                <p className="text-[11px] text-zinc-600 leading-relaxed pt-1">
                  Refunds follow the tiered cancellation policy based on the notice given before your booking start.
                  Service and payment processing fees are never refunded.
                </p>
              </div>
            )}
            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <button onClick={confirmCancel} disabled={busy} data-testid="cancel-confirm-btn" className="btn-primary flex-1 text-sm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Confirm cancellation
              </button>
              <button onClick={() => setCancelOpen(false)} data-testid="cancel-dismiss-btn" className="btn-secondary text-sm">Keep booking</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ResumePayment({ bk, onDone }) {
  // Continue an unpaid booking hold (§7): the backend hold lifecycle is the
  // authority — checkout-with-otp is idempotent (the existing PaymentIntent is
  // reused, never duplicated), the original hold timer keeps running, and an
  // expired hold is rejected with an honest message + preserved selections.
  const [payment, setPayment] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);
  const cur = (bk.currency || "usd").toUpperCase();

  const resume = async () => {
    setBusy(true);
    try {
      const p = await api.checkoutWithOtp(bk.booking_id);
      if (p.already) { toast.success("This booking is already paid"); onDone?.(); return; }
      setPayment(p);
    } catch (e) {
      if (e.status === 409) setExpired(true);
      else toast.error(e.message);
    } finally { setBusy(false); }
  };

  if (expired) {
    const rebook = bk.commercial
      ? `/p/${bk.property_code}`
      : `/spot/${bk.spot_id}?start=${encodeURIComponent(bk.start_time)}&end=${encodeURIComponent(bk.end_time)}`;
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="hold-expired-panel">
        <p className="text-sm font-semibold text-amber-300">This checkout hold expired</p>
        <p className="mt-1.5 text-xs text-zinc-400 leading-relaxed">
          The space was released and is available again — you were not charged. Your previous dates are pre-filled if you book again now.
        </p>
        <Link to={rebook} data-testid="hold-expired-rebook" className="btn-primary w-full text-sm mt-3 inline-flex justify-center">Choose a new time</Link>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="resume-payment">
      {!payment ? (
        <>
          <button onClick={resume} disabled={busy} data-testid="complete-payment-btn" className="btn-primary w-full text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Continue to payment · {fmtMoney(bk.total, cur)}
          </button>
          {bk.hold_expires_at && (
            <p className="text-[11px] text-zinc-600 leading-relaxed" data-testid="resume-hold-note">
              Your original hold is still running (until {fmtDateTimeZone(bk.hold_expires_at, "UTC")} UTC) — continuing resumes the same booking, nothing new is created.
            </p>
          )}
        </>
      ) : (
        <div data-testid="resume-payment-panel">
          <p className="meta-label mb-2.5">Complete your booking</p>
          <div className="mb-4 space-y-1.5 text-sm">
            <div className="flex justify-between text-zinc-400"><span>Rate plan</span><span data-testid="resume-rate-plan" className="capitalize">{bk.pricing_tier || "hourly"} · {bk.hours}h</span></div>
            <div className="flex justify-between text-zinc-400"><span>Interval</span><span data-testid="resume-interval" className="text-right text-xs">{fmtDateTimeZone(bk.start_time, bk.listing_timezone)} → {fmtDateTimeZone(bk.end_time, bk.listing_timezone)}</span></div>
            {(bk.pricing_periods || []).map((p, i) => (
              <div key={i} className="flex justify-between text-zinc-500 pl-3 text-[13px]" data-testid={`resume-period-${i}`}><span>· {p.label}</span><span>{fmtMoney(p.parking_cents / 100, cur)}</span></div>
            ))}
            <div className="flex justify-between text-zinc-400"><span>Base fare</span><span>{fmtMoney(bk.subtotal, cur)}</span></div>
            <div className="flex justify-between text-zinc-400"><span>Service fee</span><span>{fmtMoney(bk.platform_fee + (bk.payment_handling_cents || 0) / 100, cur)}</span></div>
            <div className="flex justify-between text-zinc-400"><span>Taxes</span><span data-testid="resume-tax">{bk.tax_state === "calculated" ? fmtMoney(bk.tax_amount ?? (bk.tax_cents || 0) / 100, cur) : bk.tax_state === "location_missing" ? "Unavailable — listing address incomplete" : "Calculated at confirmation"}</span></div>
            <div className="flex justify-between font-display font-bold border-t border-[#1F2328] pt-2"><span>Total</span><span data-testid="resume-total">{fmtMoney(bk.total, cur)}</span></div>
          </div>
          <StripePayment
            clientSecret={payment.client_secret}
            publishableKey={payment.publishable_key}
            amountLabel={fmtMoney(payment.amount, cur)}
            onSuccess={() => { toast.success("Card authorized — your arrival code is ready"); onDone?.(); }}
            onError={(m) => toast.error(m)}
          />
        </div>
      )}
    </div>
  );
}

function ExtensionPanel({ bk, onDone }) {
  // P1: extend an active session. Authorization via a separate PaymentIntent
  // (StripePayment) — the session end only moves after the backend confirms auth.
  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState(30);
  const [ext, setExt] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.extendBooking(bk.booking_id, minutes);
      setExt(r);
    } catch (e) {
      toast.error(e.message);
      setOpen(false);
      onDone?.();
    } finally { setBusy(false); }
  };
  const confirmed = async () => {
    try {
      await api.confirmExtension(bk.booking_id, ext.extension_id);
      toast.success("Parking extended");
      setOpen(false);
      setExt(null);
      onDone?.();
    } catch (e) { toast.error(e.message); }
  };

  const cur = (bk.currency || "usd").toUpperCase();
  return (
    <div className="surface-flat p-6" data-testid="extension-panel">
      {!open ? (
        <button onClick={() => setOpen(true)} className="btn-secondary w-full text-sm" data-testid="extend-parking-btn">
          Extend parking
        </button>
      ) : !ext ? (
        <div data-testid="extension-modal">
          <p className="font-bold text-sm mb-1">Extend your session</p>
          <p className="text-xs text-zinc-500 mb-4">Charged at this spot's hourly rate + platform fee. The amount is authorized now and captured when your session ends.</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {[15, 30, 60, 120].map((m) => (
              <button key={m} onClick={() => setMinutes(m)}
                      className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${minutes === m ? "bg-[#E50914] border-[#E50914] text-white" : "border-[#27272A] text-zinc-400 hover:border-zinc-500"}`}
                      data-testid={`extension-minutes-${m}`}>
                {m >= 60 ? `${m / 60}h` : `${m} min`}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={start} disabled={busy} className="btn-primary flex-1 text-sm" data-testid="extension-start-btn">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Continue to authorization
            </button>
            <button onClick={() => setOpen(false)} className="btn-secondary text-sm" data-testid="extension-cancel-btn">Cancel</button>
          </div>
        </div>
      ) : (
        <div data-testid="extension-payment">
          <p className="font-bold text-sm mb-3">Authorize {fmtMoney(ext.total_cents / 100, cur)} for +{ext.minutes} min</p>
          <StripePayment clientSecret={ext.client_secret} publishableKey={ext.publishable_key}
                         amountLabel={fmtMoney(ext.total_cents / 100, cur)}
                         onSuccess={confirmed}
                         onError={(m) => { toast.error(m); setExt(null); setOpen(false); onDone?.(); }} />
        </div>
      )}
    </div>
  );
}

// Live booking status strip (Part 14): renders ONLY states the server already
// owns — authorized (confirmed) → paid session active → ending soon → completed.
// "Ending soon" derives from the server-authoritative session_ends_at and the
// server clock offset; nothing is invented client-side and no fake transitions
// are rendered. The current step carries a subtle pulse; reduced-motion users
// get a static strip via the global prefers-reduced-motion rule.
function LiveStatusStrip({ bk }) {
  const steps = ["Confirmed", "Active", "Ending soon", "Completed"];
  const completed = !!bk.session_completed_at || bk.status === "completed";
  const active = bk.status === "paid" && !!bk.session_started_at && !bk.session_completed_at;
  let endingSoon = false;
  if (active && bk.session_ends_at && bk.server_now) {
    const offset = Date.now() - new Date(bk.server_now).getTime();
    endingSoon = new Date(bk.session_ends_at).getTime() - (Date.now() - offset) <= 30 * 60000;
  }
  const idx = completed ? 3 : endingSoon ? 2 : active ? 1 : 0;
  return (
    <ol className="mt-6 flex items-center gap-0" data-testid="live-status-strip" aria-label="Booking progress">
      {steps.map((label, i) => (
        <li key={label} className="flex items-center flex-1 last:flex-none" data-testid={`live-step-${label.toLowerCase().replace(/\s+/g, "-")}`}
            aria-current={i === idx ? "step" : undefined}>
          <div className="flex flex-col items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-full transition-colors ${
              i < idx ? "bg-emerald-500" : i === idx ? "bg-[#E50914] animate-pulse" : "bg-[#27272A]"}`} aria-hidden="true" />
            <span className={`text-[9px] font-bold uppercase tracking-[0.12em] whitespace-nowrap ${
              i === idx ? "text-white" : i < idx ? "text-zinc-500" : "text-zinc-700"}`}>{label}</span>
          </div>
          {i < steps.length - 1 && <div className={`h-px flex-1 mx-2 mb-4 ${i < idx ? "bg-emerald-500/50" : "bg-[#1F2328]"}`} aria-hidden="true" />}
        </li>
      ))}
    </ol>
  );
}


function ParkingTimer({ bk }) {
  // Server-authoritative: remaining time is computed from the server's clock
  // (server_now offset), not the device clock. The 15s booking poll re-syncs the
  // offset, so refresh/reopen/sleep/timezone changes stay accurate.
  const [remainingMs, setRemainingMs] = useState(null);
  useEffect(() => {
    if (!bk.session_ends_at || !bk.server_now) return undefined;
    const offset = new Date(bk.server_now).getTime() - Date.now();
    const tick = () => setRemainingMs(new Date(bk.session_ends_at).getTime() - (Date.now() + offset));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [bk.session_ends_at, bk.server_now]);

  const fmt = (ms) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    return [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60].map((n) => String(n).padStart(2, "0")).join(":");
  };
  const endsAt = bk.session_ends_at ? new Date(bk.session_ends_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—";

  return (
    <div className="surface-flat p-7 text-center" data-testid="parking-timer">
      <p className="meta-label !text-emerald-400">Parking active</p>
      {remainingMs !== null && remainingMs > 0 ? (
        <>
          <p className="font-mono-otp text-4xl tracking-[0.12em] mt-4" data-testid="timer-remaining">{fmt(remainingMs)}</p>
          <p className="mt-3 text-xs text-zinc-500">remaining · Parking ends at <span className="text-zinc-300 font-semibold" data-testid="timer-ends">{endsAt}</span></p>
        </>
      ) : (
        <p className="mt-4 text-sm text-zinc-300 font-semibold" data-testid="timer-expired">Time is up — your session is being closed.</p>
      )}
    </div>
  );
}

function ReviewCard({ bookingId, state, onDone }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!rating) { toast.error("Choose a star rating first"); return; }
    setBusy(true);
    try {
      await api.createReview({ booking_id: bookingId, rating, comment: comment.trim() || null });
      toast.success("Thanks — your review is live");
      await onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (state.review) {
    return (
      <div className="surface-flat p-6" data-testid="review-submitted-card">
        <p className="meta-label mb-2">Your review</p>
        <div className="flex items-center gap-1 text-amber-400" aria-label={`${state.review.rating} out of 5 stars`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className={`w-4 h-4 ${i < state.review.rating ? "fill-amber-400" : "text-zinc-700"}`} aria-hidden="true" />
          ))}
        </div>
        {state.review.comment && <p className="mt-2 text-sm text-zinc-300 leading-relaxed">{state.review.comment}</p>}
        <p className="mt-2 text-[11px] text-zinc-600">Thanks for the feedback — it's shown on the listing.</p>
      </div>
    );
  }
  if (!state.eligible) return null;
  return (
    <div className="surface-flat p-6" data-testid="review-form-card">
      <p className="meta-label mb-1">How was your parking experience?</p>
      <div className="mt-3 flex gap-1.5" role="radiogroup" aria-label="Star rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={rating === n} data-testid={`review-star-${n}`}
                  onClick={() => setRating(n)} aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  className="p-1 transition-transform hover:scale-110">
            <Star className={`w-6 h-6 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} aria-hidden="true" />
          </button>
        ))}
      </div>
      <textarea data-testid="review-comment" rows={3} maxLength={1000} value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="input-dark !h-auto py-3 mt-3" placeholder="Optional comment — easy to find? As described?" aria-label="Review comment" />
      <button onClick={submit} disabled={busy} data-testid="review-submit-btn" className="btn-primary w-full mt-3 text-sm">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit review
      </button>
    </div>
  );
}
